import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  ROTULO_DO_ESTADO,
  ROTULO_DO_PEDIDO,
  anexarArquivo,
  cancelarPedido,
  criarPedido,
  decidirPedido,
  diaLongo,
  diaMes,
  hojeBrasilia,
  lerEu,
  lerPedidos,
  lerQuadro,
  somarDias,
  urlDoArquivo,
} from '../../escala';
import type {
  ControleDaPessoa,
  EstadoDoPedido,
  PedidoDaEscala,
  PessoaDaEscala,
  TipoDePedido,
} from '../../escala';

/**
 * PEDIDOS DA ESCALA (dono, 24/10/2026): "os atendentes podem pedir folga, trocar por dentro do
 * sistema e o Carlos trocar, pedir ou agendar férias, e subir atestados".
 *
 *   equipe          faz o pedido, vê os seus (e as trocas em que um colega a chamou), cancela
 *                   enquanto aguarda, anexa a foto/PDF do atestado;
 *   Carlos/Nicolas  veem todos — os que aguardam primeiro — e aprovam (o quadro muda sozinho)
 *                   ou recusam com a resposta.
 */

const TIPOS: readonly { id: TipoDePedido; rotulo: string; texto: string }[] = [
  { id: 'FOLGA', rotulo: 'Folga', texto: 'Um dia (ou mais) de folga.' },
  {
    id: 'TROCA',
    rotulo: 'Trocar folga',
    texto: 'Passar a folga para outro dia, sozinho ou com um colega.',
  },
  { id: 'FERIAS', rotulo: 'Férias', texto: 'Pedir ou agendar as férias.' },
  { id: 'ATESTADO', rotulo: 'Atestado', texto: 'Mandar a foto ou o PDF do atestado.' },
];

const descricao = (p: PedidoDaEscala): string => {
  if (p.tipo === 'TROCA')
    return p.trocaPessoa
      ? `${diaMes(p.de)} e ${diaMes(p.trocaDia ?? p.de)} trocados com ${p.trocaPessoa}`
      : `Folga de ${diaMes(p.de)} passa para ${diaMes(p.trocaDia ?? p.de)}`;
  const periodo = p.de === p.ate ? diaLongo(p.de) : `${diaMes(p.de)} a ${diaMes(p.ate)}`;
  return `${periodo}${p.compensa ? ` · compensa o feriado de ${diaMes(p.compensa)}` : ''}`;
};

export function AbaPedidos({
  sessao,
  aoMudar,
  aoSaberPermissao,
}: {
  sessao: Sessao;
  aoMudar: () => void;
  aoSaberPermissao: (pode: boolean) => void;
}) {
  const [pedidos, setPedidos] = useState<PedidoDaEscala[] | null>(null);
  const [erro, setErro] = useState(false);
  const [editor, setEditor] = useState(false);
  const [eu, setEu] = useState<PessoaDaEscala | null>(null);
  const [controle, setControle] = useState<ControleDaPessoa | null>(null);
  const [pessoas, setPessoas] = useState<PessoaDaEscala[]>([]);
  const [filtro, setFiltro] = useState<EstadoDoPedido | ''>('');
  const [novo, setNovo] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('novo') === '1',
  );
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    void lerPedidos(filtro || undefined).then((r) =>
      r.ok ? setPedidos(r.dados.pedidos) : setErro(true),
    );
  }, [filtro]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    void lerQuadro().then((r) => {
      if (!r.ok) return;
      setEditor(r.dados.podeEditar);
      aoSaberPermissao(r.dados.podeEditar);
      setPessoas(r.dados.pessoas.filter((p) => p.ativo));
    });
    void lerEu().then((r) => {
      if (!r.ok) return;
      setEu(r.dados.pessoa);
      setControle(r.dados.controle);
    });
  }, [aoSaberPermissao]);

  const depois = (texto: string) => {
    setAviso({ tom: 'ok', texto });
    carregar();
    aoMudar();
  };

  if (erro) return <ErroDeArea oQue="os pedidos" aoTentar={carregar} />;

  return (
    <div className="qh-corpo">
      <div className="qh-barra">
        <label className="qh-campo-inline">
          Mostrar
          <select value={filtro} onChange={(e) => setFiltro(e.target.value as EstadoDoPedido | '')}>
            <option value="">Todos</option>
            {(['PENDENTE', 'APROVADO', 'RECUSADO', 'CANCELADO'] as const).map((e) => (
              <option key={e} value={e}>
                {ROTULO_DO_ESTADO[e]}
              </option>
            ))}
          </select>
        </label>
        {(eu || editor) && !novo && (
          <button type="button" className="qh-btn qh-btn-pri" onClick={() => setNovo(true)}>
            <Icone nome="plus" tamanho={15} />
            {editor ? 'Novo pedido / agendar férias' : 'Fazer um pedido'}
          </button>
        )}
      </div>

      {aviso && (
        <div
          className={`qh-aviso qh-aviso-${aviso.tom}`}
          role={aviso.tom === 'erro' ? 'alert' : 'status'}
        >
          {aviso.texto}
          <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
            <Icone nome="x" tamanho={14} />
          </button>
        </div>
      )}

      {novo && (
        <NovoPedido
          sessao={sessao}
          eu={eu}
          editor={editor}
          pessoas={pessoas}
          feriadosAPagar={controle?.feriadosAPagar ?? []}
          aoCancelar={() => setNovo(false)}
          aoCriar={(texto) => {
            setNovo(false);
            depois(texto);
          }}
          aoErro={(texto) => setAviso({ tom: 'erro', texto })}
        />
      )}

      {!eu && !editor && (
        <EstadoVazio
          icone="user"
          titulo="Você ainda não está no quadro"
          texto="Peça ao Carlos para colocar você no quadro de horário — aí dá para fazer pedidos."
        />
      )}

      {pedidos === null ? (
        <div className="qh-carregando">Carregando os pedidos…</div>
      ) : pedidos.length === 0 ? (
        (eu || editor) && (
          <EstadoVazio
            icone="file"
            titulo="Nenhum pedido"
            texto="Os pedidos de folga, troca, férias e atestado aparecem aqui."
          />
        )
      ) : (
        <ul className="qh-pedidos">
          {pedidos.map((p) => (
            <CartaoDoPedido
              key={p.id}
              p={p}
              sessao={sessao}
              editor={editor}
              meu={!!eu && p.pessoaId === eu.id}
              aoMudar={depois}
              aoErro={(texto) => setAviso({ tom: 'erro', texto })}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CartaoDoPedido({
  p,
  sessao,
  editor,
  meu,
  aoMudar,
  aoErro,
}: {
  p: PedidoDaEscala;
  sessao: Sessao;
  editor: boolean;
  meu: boolean;
  aoMudar: (texto: string) => void;
  aoErro: (texto: string) => void;
}) {
  const [resposta, setResposta] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const decidir = async (aprovar: boolean) => {
    if (!aprovar && !resposta.trim() && !window.confirm('Recusar sem escrever o motivo?')) return;
    setOcupado(true);
    const r = await decidirPedido(sessao, p.id, aprovar, resposta.trim() || null);
    setOcupado(false);
    if (r.tipo === 'confirmada')
      aoMudar(
        aprovar ? `Aprovado: ${p.pessoa} — o quadro já foi atualizado.` : `Recusado: ${p.pessoa}.`,
      );
    else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu no meio — confira o pedido.');
  };
  const cancelar = async () => {
    if (!window.confirm('Cancelar este pedido?')) return;
    setOcupado(true);
    const r = await cancelarPedido(sessao, p.id);
    setOcupado(false);
    if (r.tipo === 'confirmada') aoMudar('Pedido cancelado.');
    else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu no meio — confira o pedido.');
  };
  const anexar = async (f: File | undefined) => {
    if (!f) return;
    setOcupado(true);
    const r = await anexarArquivo(sessao, p.id, f);
    setOcupado(false);
    if (r.tipo === 'confirmada') aoMudar('Atestado anexado.');
    else aoErro(r.tipo === 'recusada' ? r.mensagem : 'A internet caiu no meio — confira o pedido.');
  };

  return (
    <li className={`qh-pedido qh-pedido-${p.estado.toLowerCase()}`}>
      <div className="qh-pedido-topo">
        <span className={`qh-tipo qh-tipo-${p.tipo.toLowerCase()}`}>
          {ROTULO_DO_PEDIDO[p.tipo]}
        </span>
        <strong>{p.pessoa}</strong>
        <span className={`qh-estado qh-estado-${p.estado.toLowerCase()}`}>
          {ROTULO_DO_ESTADO[p.estado]}
        </span>
      </div>
      <p className="qh-pedido-desc">{descricao(p)}</p>
      {p.motivo && <p className="qh-pedido-motivo">“{p.motivo}”</p>}
      <p className="qh-pedido-meta">
        Pedido por {p.pedidoPor} em{' '}
        {new Date(p.criadoEm).toLocaleString('pt-BR', {
          timeZone: 'America/Sao_Paulo',
          dateStyle: 'short',
          timeStyle: 'short',
        })}
        {p.decididoPor && p.decididoEm && (
          <>
            {' '}
            · {p.estado === 'CANCELADO' ? 'cancelado' : 'decidido'} por {p.decididoPor}
          </>
        )}
      </p>
      {p.resposta && <p className="qh-pedido-resposta">Resposta: {p.resposta}</p>}
      {p.arquivos.length > 0 && (
        <div className="qh-arquivos">
          {p.arquivos.map((a, i) => (
            <a
              key={a.id}
              href={urlDoArquivo(a.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="qh-arquivo"
            >
              <Icone nome={a.mime === 'application/pdf' ? 'file' : 'image'} tamanho={15} />
              Atestado {p.arquivos.length > 1 ? String(i + 1) : ''} (
              {Math.max(1, Math.round(a.tamanho / 1024))} KB)
            </a>
          ))}
        </div>
      )}
      {p.tipo === 'ATESTADO' &&
        (meu || editor) &&
        p.estado !== 'CANCELADO' &&
        p.estado !== 'RECUSADO' && (
          <label className="qh-btn qh-btn-sec qh-btn-arquivo">
            <Icone nome="cam" tamanho={15} />
            {p.arquivos.length ? 'Anexar mais uma foto/PDF' : 'Anexar a foto ou PDF do atestado'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              capture="environment"
              disabled={ocupado}
              onChange={(e) => void anexar(e.target.files?.[0])}
            />
          </label>
        )}
      {p.estado === 'PENDENTE' && editor && (
        <div className="qh-decidir">
          <input
            value={resposta}
            maxLength={500}
            placeholder="Resposta (opcional na aprovação)"
            onChange={(e) => setResposta(e.target.value)}
            aria-label="Resposta"
          />
          <button
            type="button"
            className="qh-btn qh-btn-ok"
            disabled={ocupado}
            onClick={() => void decidir(true)}
          >
            <Icone nome="check" tamanho={15} />
            Aprovar
          </button>
          <button
            type="button"
            className="qh-btn qh-btn-perigo"
            disabled={ocupado}
            onClick={() => void decidir(false)}
          >
            <Icone nome="x" tamanho={15} />
            Recusar
          </button>
        </div>
      )}
      {p.estado === 'PENDENTE' && meu && !editor && (
        <button
          type="button"
          className="qh-btn qh-btn-sec qh-btn-curto"
          disabled={ocupado}
          onClick={() => void cancelar()}
        >
          Cancelar pedido
        </button>
      )}
    </li>
  );
}

function NovoPedido({
  sessao,
  eu,
  editor,
  pessoas,
  feriadosAPagar,
  aoCancelar,
  aoCriar,
  aoErro,
}: {
  sessao: Sessao;
  eu: PessoaDaEscala | null;
  editor: boolean;
  pessoas: readonly PessoaDaEscala[];
  feriadosAPagar: readonly string[];
  aoCancelar: () => void;
  aoCriar: (texto: string) => void;
  aoErro: (texto: string) => void;
}) {
  const hoje = hojeBrasilia();
  const [tipo, setTipo] = useState<TipoDePedido>('FOLGA');
  const [pessoaId, setPessoaId] = useState(eu?.id ?? '');
  const [de, setDe] = useState(somarDias(hoje, 1));
  const [ate, setAte] = useState(somarDias(hoje, 1));
  const [trocaDia, setTrocaDia] = useState(somarDias(hoje, 2));
  const [comColega, setComColega] = useState('');
  const [compensa, setCompensa] = useState('');
  const [motivo, setMotivo] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [chave] = useState(() => crypto.randomUUID());
  const [enviando, setEnviando] = useState(false);

  const escolherTipo = (t: TipoDePedido) => {
    setTipo(t);
    if (t === 'ATESTADO') {
      setDe(hoje);
      setAte(hoje);
    } else if (de < hoje) {
      setDe(somarDias(hoje, 1));
      setAte(somarDias(hoje, 1));
    }
  };

  const enviar = async () => {
    if (
      tipo === 'ATESTADO' &&
      !arquivo &&
      !window.confirm('Enviar sem a foto do atestado? Dá para anexar depois.')
    )
      return;
    setEnviando(true);
    const r = await criarPedido(sessao, {
      chave,
      tipo,
      pessoaId: editor && pessoaId ? pessoaId : null,
      de,
      ate: tipo === 'TROCA' ? de : ate,
      trocaDia: tipo === 'TROCA' ? trocaDia : null,
      trocaPessoaId: tipo === 'TROCA' && comColega ? comColega : null,
      compensa: tipo === 'FOLGA' && compensa ? compensa : null,
      motivo: motivo.trim() || null,
    });
    if (r.tipo !== 'confirmada') {
      setEnviando(false);
      return aoErro(
        r.tipo === 'recusada'
          ? r.mensagem
          : 'A internet caiu no meio — confira se o pedido entrou.',
      );
    }
    if (arquivo) {
      const a = await anexarArquivo(sessao, r.dados.id, arquivo);
      if (a.tipo !== 'confirmada') {
        setEnviando(false);
        return aoErro(
          `O pedido entrou, mas o arquivo não: ${a.tipo === 'recusada' ? a.mensagem : 'a internet caiu'}. Anexe de novo no pedido.`,
        );
      }
    }
    setEnviando(false);
    aoCriar('Pedido enviado — o Carlos vai ver e responder.');
  };

  const colegas = pessoas.filter((p) => p.id !== (editor ? pessoaId : eu?.id));

  return (
    <div className="qh-novo">
      <h2>{editor ? 'Novo pedido' : 'Fazer um pedido'}</h2>
      <div className="qh-tipos" role="radiogroup" aria-label="Tipo de pedido">
        {TIPOS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={tipo === t.id}
            className={`qh-tipo-opcao${tipo === t.id ? ' qh-tipo-opcao-on' : ''}`}
            onClick={() => escolherTipo(t.id)}
          >
            <strong>{t.rotulo}</strong>
            <small>{t.texto}</small>
          </button>
        ))}
      </div>
      {editor && (
        <label className="qh-campo">
          Para quem
          <select value={pessoaId} onChange={(e) => setPessoaId(e.target.value)}>
            <option value="">{eu ? `Eu (${eu.nome})` : 'Escolha a pessoa'}</option>
            {pessoas
              .filter((p) => p.id !== eu?.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
          </select>
        </label>
      )}
      <div className="qh-linha-campos">
        <label className="qh-campo">
          {tipo === 'TROCA' ? 'Minha folga de' : 'De'}
          <input
            type="date"
            value={de}
            {...(tipo === 'ATESTADO' ? { max: hoje, min: somarDias(hoje, -30) } : { min: hoje })}
            onChange={(e) => {
              setDe(e.target.value);
              if (ate < e.target.value) setAte(e.target.value);
            }}
          />
        </label>
        {tipo === 'TROCA' ? (
          <label className="qh-campo">
            Passa para
            <input
              type="date"
              value={trocaDia}
              min={hoje}
              onChange={(e) => setTrocaDia(e.target.value)}
            />
          </label>
        ) : (
          <label className="qh-campo">
            Até
            <input
              type="date"
              value={ate}
              min={de}
              {...(tipo === 'ATESTADO' ? { max: hoje } : {})}
              onChange={(e) => setAte(e.target.value)}
            />
          </label>
        )}
      </div>
      {tipo === 'TROCA' && (
        <label className="qh-campo">
          Trocar com um colega? (opcional)
          <select value={comColega} onChange={(e) => setComColega(e.target.value)}>
            <option value="">Não — só mudo a minha folga de dia</option>
            {colegas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
          <small className="qh-ajuda">
            Com colega: nos dois dias, você fica com o turno dele e ele com o seu.
          </small>
        </label>
      )}
      {tipo === 'FOLGA' && feriadosAPagar.length > 0 && (
        <label className="qh-campo">
          É folga de feriado trabalhado?
          <select value={compensa} onChange={(e) => setCompensa(e.target.value)}>
            <option value="">Não</option>
            {feriadosAPagar.map((f) => (
              <option key={f} value={f}>
                Sim — compensa o feriado de {diaMes(f)}
              </option>
            ))}
          </select>
        </label>
      )}
      {tipo === 'ATESTADO' && (
        <label className="qh-campo">
          Foto ou PDF do atestado
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
          />
          <small className="qh-ajuda">Só você, o Carlos e o Nicolas veem o atestado.</small>
        </label>
      )}
      <label className="qh-campo">
        {tipo === 'ATESTADO' ? 'Observação (opcional)' : 'Motivo (opcional)'}
        <textarea
          value={motivo}
          maxLength={500}
          rows={2}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      <div className="qh-novo-acoes">
        <button
          type="button"
          className="qh-btn qh-btn-sec"
          onClick={aoCancelar}
          disabled={enviando}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="qh-btn qh-btn-pri"
          onClick={() => void enviar()}
          disabled={enviando || (editor && !pessoaId && !eu)}
        >
          {enviando ? 'Enviando…' : 'Enviar pedido'}
        </button>
      </div>
    </div>
  );
}
